import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { BellPlus } from "lucide-react";

const defaultRemindAt = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  return d.toISOString().slice(0, 16);
};

export default function AddReminderModal({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ note: "", remind_at: defaultRemindAt(), severity: "Normal" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.note.trim()) return toast.error("Note is required");
    setSaving(true);
    try {
      await endpoints.createReminder({ ...form, remind_at: new Date(form.remind_at).toISOString() });
      toast.success("Reminder set");
      onCreated?.();
      onOpenChange(false);
      setForm({ note: "", remind_at: defaultRemindAt(), severity: "Normal" });
    } catch {
      toast.error("Could not save reminder");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-add-reminder" className="sm:max-w-[480px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white flex items-center justify-center shadow-md shadow-fuchsia-500/20">
              <BellPlus className="h-4.5 w-4.5" />
            </div>
            <div>
              <DialogTitle>Add Reminder</DialogTitle>
              <DialogDescription>Set a nudge so nothing slips through.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="rem-note">Note</Label>
            <Textarea data-testid="reminder-note-input" id="rem-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Follow up with Helios on audit scope" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rem-time">Remind at</Label>
              <Input data-testid="reminder-time-input" id="rem-time" type="datetime-local" value={form.remind_at} onChange={(e) => setForm({ ...form, remind_at: e.target.value })} />
            </div>
            <div>
              <Label>Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="reminder-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Important">Important</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="reminder-submit-button" type="submit" disabled={saving} className="brand-gradient text-white">
              {saving ? "Saving..." : "Add reminder"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
